import React from 'react';
import { ArrowUpRight, X, Globe} from 'lucide-react';
import { SourcesDrawerProps } from '@/types/searchResults';
import {
  Drawer,
  DrawerContent,
  DrawerClose,
} from '@/components/ui/drawer';

export const SourcesDrawer: React.FC<SourcesDrawerProps & { isOpen: boolean; onClose: () => void }> = ({
  sources,
  isOpen,
  onClose,
  isDarkMode,
}) => {
  const getDomain = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  if (!sources || sources.length === 0 || !isOpen) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DrawerContent
      showHandle={false}
      className={`mx-auto flex w-full max-w-2xl flex-col rounded-t-3xl pb-5 shadow-2xl backdrop-blur-lg h-[70vh] ${
          isDarkMode
            ? 'bg-[#1b1b1b] text-white border border-white/10'
            : 'bg-white text-[#0f0f0f] border border-black/10'
        }`}
      >
        <div className="px-5 pt-4 pb-3 ml-1 my-2">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">SOURCES</div>
            <DrawerClose asChild>
            </DrawerClose>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 xs:px-4 pb-5">
          <div className="space-y-4">
            {sources.slice(0, 3).map((source, index) => (
              <a
                key={`${source.url}-${index}`}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block rounded-2xl bg-transparent px-4 py-3 transition ${
                  isDarkMode
                    ? 'hover:bg-white/10'
                    : 'hover:bg-black/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center overflow-hidden ${
                      isDarkMode
                        ? 'bg-black/20'
                        : 'bg-white'
                    }`}
                  >
                    {source.favicon ? (
                      <img
                        src={source.favicon}
                        alt=""
                        className="h-8 w-8 object-contain rounded-xl"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Globe className={`h-4 w-4 ${isDarkMode ? 'text-white/60' : 'text-black/50'}`} />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-black/60'}`}>
                      {getDomain(source.url)}
                    </div>
                    <div className={`mt-1 text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      {source.title || ""}
                      
                    </div>
                  </div>

                </div>
              </a>
            ))}

            {sources.slice(3).map((source, index) => (
              <a
                key={`${source.url}-more-${index}`}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block rounded-2xl bg-transparent px-4 py-3 transition ${
                  isDarkMode
                    ? 'hover:bg-white/10'
                    : 'hover:bg-black/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center overflow-hidden ${
                      isDarkMode
                        ? 'bg-black/20'
                        : 'bg-white'
                    }`}
                  >
                    {source.favicon ? (
                      <img
                        src={source.favicon}
                        alt=""
                        className="h-8 w-8 object-contain rounded-xl"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Globe className={`h-4 w-4 ${isDarkMode ? 'text-white/60' : 'text-black/50'}`} />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-black/60'}`}>
                      {getDomain(source.url)}
                    </div>
                    <div className={`mt-1 text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      {source.title || getDomain(source.url)}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
